import { useState, useCallback, useEffect } from 'react';
import styled from 'foundations-theming/utils';
import { Panel, PanelSection } from 'foundations-components/transitional/components/Panel';
import { PanelBody } from 'foundations-components/transitional/components/PanelBody';
import { PanelFooter } from 'foundations-components/transitional/components/PanelFooter';
import { CloseButton } from 'foundations-components/transitional/components/CloseButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { H4 } from 'foundations-components/transitional/components/H4';
import { Link } from 'foundations-components/transitional/components/Link';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { Small } from 'foundations-components/transitional/components/Small';
import { Flex } from 'foundations-components/components/Flex';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { StatusDot } from 'foundations-components/transitional/components/StatusDot';
import { HR } from 'foundations-components/transitional/components/HR';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { InvoiceIcon } from 'foundations-assets/icons/components/InvoiceIcon';
import { DealsIcon } from 'foundations-assets/icons/components/DealsIcon';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useIsPillBorderRadius } from '../hooks/useIsPillBorderRadius';
import { useNotifications } from '../hooks/useNotifications';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const PANEL_WIDTH_BY_BREAKPOINT = {
  mobile: '100%',
  tablet: '60%',
  desktop: 500
};
const NOTIFICATIONS_LIMIT = 4;
const TYPE_CONFIG = {
  BILLING: {
    icon: InvoiceIcon,
    iconColor: 'fill-accent-orange-default',
    backgroundColor: 'fill-accent-orange-subtle-alt',
    labelKey: 'app.notifications.type.billing'
  },
  DEALS: {
    icon: DealsIcon,
    iconColor: 'fill-accent-blue-default',
    backgroundColor: 'fill-accent-blue-subtle-alt',
    labelKey: 'app.notifications.type.deals'
  }
};
function formatRelativeTime(createdAt) {
  const minutes = Math.floor((Date.now() - createdAt) / 60000);
  if (minutes < 1) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "app.notifications.time.justNow"
    });
  }
  if (minutes < 60) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "app.notifications.time.minutesAgo",
      options: {
        count: minutes
      }
    });
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "app.notifications.time.hoursAgo",
      options: {
        count: hours
      }
    });
  }
  const days = Math.floor(hours / 24);
  if (days === 1) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "app.notifications.time.yesterday"
    });
  }
  if (days < 7) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "app.notifications.time.daysAgo",
      options: {
        count: days
      }
    });
  }
  return /*#__PURE__*/_jsx(FormattedMessage, {
    message: "app.notifications.time.weeksAgo",
    options: {
      count: Math.floor(days / 7)
    }
  });
}
function NotificationsHeading({
  unreadCount
}) {
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    gap: "100",
    children: [/*#__PURE__*/_jsx(H4, {
      color: "text-core-default",
      marginBlock: "0",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app.notifications.heading"
      })
    }), unreadCount > 0 && /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "fill-alert-default",
      borderRadius: "full",
      blockSize: "20px",
      minInlineSize: "20px",
      paddingInline: "100",
      children: /*#__PURE__*/_jsx(Small, {
        color: "text-alert-on-fill-default",
        weight: "bold",
        children: unreadCount
      })
    })]
  });
}
const NotificationCard = styled.div.withConfig({
  displayName: "NotificationsPanel__NotificationCard"
})(({
  theme,
  $isPill
}) => ({
  position: 'relative',
  cursor: 'pointer',
  borderRadius: $isPill ? theme.borderRadius['0'] : theme.borderRadius['200'],
  transition: 'background-color 200ms ease-in-out',
  '&:hover': {
    backgroundColor: theme.color['fill-surface-default-hover']
  }
}));
function NotificationItem({
  notification,
  onRemove,
  onMarkAsRead
}) {
  var _notification$ctaLink;
  const isPill = useIsPillBorderRadius();
  const [isHovered, setIsHovered] = useState(false);
  const config = TYPE_CONFIG[notification.notificationType];
  const openNotification = () => {
    if (!notification.isRead) {
      onMarkAsRead(notification.id);
    }
    if (notification.ctaLink) {
      window.open(notification.ctaLink, '_blank', 'noopener,noreferrer');
    }
  };
  return /*#__PURE__*/_jsxs(NotificationCard, {
    $isPill: isPill,
    onClick: openNotification,
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    children: [/*#__PURE__*/_jsxs(Flex, {
      gap: "300",
      paddingBlock: "500",
      paddingInline: "600",
      alignItems: "start",
      children: [/*#__PURE__*/_jsx(Flex, {
        flexShrink: 0,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: config.backgroundColor,
        borderRadius: isPill ? '0' : '200',
        padding: "200",
        children: /*#__PURE__*/_jsx(IconHolder, {
          icon: config.icon,
          color: config.iconColor,
          size: "400",
          compact: true
        })
      }), /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        flexGrow: "1",
        gap: "100",
        minInlineSize: "0px",
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          gap: "100",
          children: [!notification.isRead && /*#__PURE__*/_jsx(StatusDot, {
            use: "danger"
          }), /*#__PURE__*/_jsx(Link, {
            href: (_notification$ctaLink = notification.ctaLink) !== null && _notification$ctaLink !== void 0 ? _notification$ctaLink : undefined,
            use: "dark",
            color: "text-core-default",
            underline: true,
            typography: "heading-50",
            target: "_blank",
            rel: "noopener noreferrer",
            onClick: event => {
              event.stopPropagation();
              if (!notification.isRead) {
                onMarkAsRead(notification.id);
              }
            },
            children: notification.title
          })]
        }), /*#__PURE__*/_jsxs(Small, {
          color: "text-core-subtle",
          children: [formatRelativeTime(notification.createdAt), " \xB7", ' ', /*#__PURE__*/_jsx(FormattedMessage, {
            message: config.labelKey
          })]
        }), /*#__PURE__*/_jsx(Flex, {
          borderWidth: "100",
          borderStyle: "solid",
          borderColor: "border-core-default",
          borderRadius: isPill ? '0' : '200',
          padding: "300",
          marginBlockStart: "100",
          children: /*#__PURE__*/_jsx(Paragraph, {
            color: "text-core-default",
            typography: "body-100",
            marginBlock: "0",
            children: notification.body
          })
        })]
      })]
    }), /*#__PURE__*/_jsx(Flex, {
      position: "absolute",
      insetBlockStart: "400",
      insetInlineEnd: "400",
      visibility: isHovered ? 'visible' : 'hidden',
      pointerEvents: isHovered ? 'auto' : 'none',
      children: /*#__PURE__*/_jsx(Tooltip, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.notifications.dismiss"
        }),
        placement: "top",
        zIndex: 2147483647,
        children: /*#__PURE__*/_jsx(CloseButton, {
          size: "medium",
          inline: true,
          onClick: event => {
            event.stopPropagation();
            onRemove(notification.id);
          },
          color: "text-core-default"
        })
      })
    })]
  });
}
function NotificationsList({
  notifications,
  loading,
  loadingMore,
  hasMore,
  error,
  onLoadMore,
  onRemove,
  onMarkAsRead
}) {
  if (loading) {
    return /*#__PURE__*/_jsx(Flex, {
      justifyContent: "center",
      paddingBlock: "400",
      children: /*#__PURE__*/_jsx(LoadingSpinner, {
        size: "small"
      })
    });
  }
  if (error) {
    return /*#__PURE__*/_jsx(Alert, {
      type: "danger",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app.notifications.loadError"
      })
    });
  }
  if (notifications.length === 0) {
    return /*#__PURE__*/_jsx(Flex, {
      justifyContent: "center",
      alignItems: "center",
      paddingBlock: "800",
      paddingInline: "400",
      children: /*#__PURE__*/_jsx(Paragraph, {
        color: "text-core-default",
        typography: "body-100",
        marginBlock: "0",
        textAlign: "center",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.notifications.emptyState"
        })
      })
    });
  }
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    children: [notifications.map((notification, index) => /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      children: [index > 0 && /*#__PURE__*/_jsx(HR, {
        distance: "flush"
      }), /*#__PURE__*/_jsx(NotificationItem, {
        notification: notification,
        onRemove: onRemove,
        onMarkAsRead: onMarkAsRead
      })]
    }, notification.id)), hasMore && /*#__PURE__*/_jsx(Flex, {
      justifyContent: "end",
      paddingBlockStart: "300",
      paddingInline: "400",
      children: /*#__PURE__*/_jsx(LoadingButton, {
        use: "tertiary-light",
        size: "sm",
        loading: loadingMore,
        onClick: onLoadMore,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.notifications.loadMore"
        })
      })
    })]
  });
}
function NotificationsFooter({
  onClose,
  onMarkAllAsRead,
  markAllDisabled
}) {
  return /*#__PURE__*/_jsxs(PanelFooter, {
    align: "between",
    paddingBlock: "400",
    children: [/*#__PURE__*/_jsx(Button, {
      use: "secondary-ghost",
      onClick: onClose,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app.notifications.close"
      })
    }), /*#__PURE__*/_jsx(Button, {
      use: "primary",
      onClick: onMarkAllAsRead,
      disabled: markAllDisabled,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app.notifications.markAllAsRead"
      })
    })]
  });
}
export function NotificationsPanel({
  onClose
}) {
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'mobile';
  const {
    notifications,
    unreadCount,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    markAsRead,
    markAllAsRead,
    remove
  } = useNotifications({
    limit: NOTIFICATIONS_LIMIT
  });
  const [panelAlert, setPanelAlert] = useState(null);
  useEffect(() => {
    if (!panelAlert) return;
    const timer = setTimeout(() => setPanelAlert(null), 3000);
    return () => clearTimeout(timer);
  }, [panelAlert]);
  const handleRemove = useCallback(notificationId => {
    remove(notificationId).catch(() => {
      setPanelAlert({
        type: 'danger',
        messageKey: 'app.notifications.dismissError'
      });
    });
  }, [remove]);
  const handleMarkAsRead = useCallback(notificationId => {
    var _markAsRead;
    (_markAsRead = markAsRead(notificationId)) === null || _markAsRead === void 0 || _markAsRead.catch(() => {
      setPanelAlert({
        type: 'danger',
        messageKey: 'app.notifications.markAsReadError'
      });
    });
  }, [markAsRead]);
  const handleMarkAllAsRead = useCallback(() => {
    markAllAsRead().catch(() => {
      setPanelAlert({
        type: 'danger',
        messageKey: 'app.notifications.markAllAsReadError'
      });
    });
  }, [markAllAsRead]);
  const footer = /*#__PURE__*/_jsx(NotificationsFooter, {
    onClose: onClose,
    onMarkAllAsRead: handleMarkAllAsRead,
    markAllDisabled: unreadCount === 0
  });
  return /*#__PURE__*/_jsx(Panel, {
    width: PANEL_WIDTH_BY_BREAKPOINT[breakpoint],
    containerZIndex: "2147483647",
    children: /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      position: "relative",
      flex: 1,
      minBlockSize: "0",
      children: [isMobile ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(Flex, {
          position: "sticky",
          insetBlockStart: "0",
          zIndex: "1",
          backgroundColor: "fill-surface-default",
          borderBlockEndWidth: "100",
          borderBlockEndStyle: "solid",
          borderBlockEndColor: "border-core-default",
          paddingInline: "400",
          paddingBlock: "300",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "200",
          children: [/*#__PURE__*/_jsx(NotificationsHeading, {
            unreadCount: unreadCount
          }), /*#__PURE__*/_jsx(CloseButton, {
            inline: true,
            onClick: onClose,
            color: "text-core-default"
          })]
        }), /*#__PURE__*/_jsx(PanelBody, {
          overflowX: "hidden",
          children: /*#__PURE__*/_jsxs(PanelSection, {
            paddingInline: "0",
            children: [/*#__PURE__*/_jsx(HR, {
              distance: "flush"
            }), /*#__PURE__*/_jsx(NotificationsList, {
              notifications: notifications,
              loading: loading,
              loadingMore: loadingMore,
              hasMore: hasMore,
              error: error,
              onLoadMore: loadMore,
              onRemove: handleRemove,
              onMarkAsRead: handleMarkAsRead
            })]
          })
        }), footer]
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(CloseButton, {
          onClick: onClose,
          color: "text-core-default"
        }), /*#__PURE__*/_jsx(PanelBody, {
          paddingInline: "0",
          overflowX: "hidden",
          children: /*#__PURE__*/_jsxs(PanelSection, {
            paddingInline: "0",
            children: [/*#__PURE__*/_jsx(Flex, {
              marginBlockEnd: "200",
              paddingInline: "400",
              children: /*#__PURE__*/_jsx(NotificationsHeading, {
                unreadCount: unreadCount
              })
            }), /*#__PURE__*/_jsx(HR, {
              distance: "flush"
            }), /*#__PURE__*/_jsx(NotificationsList, {
              notifications: notifications,
              loading: loading,
              loadingMore: loadingMore,
              hasMore: hasMore,
              error: error,
              onLoadMore: loadMore,
              onRemove: handleRemove,
              onMarkAsRead: handleMarkAsRead
            })]
          })
        }), footer]
      }), panelAlert && /*#__PURE__*/_jsx(Flex, {
        position: "absolute",
        insetBlockStart: "400",
        insetInlineStart: "400",
        insetInlineEnd: "400",
        zIndex: "2",
        boxShadow: "300",
        borderRadius: "200",
        overflowX: "hidden",
        overflowY: "hidden",
        flexDirection: "column",
        children: /*#__PURE__*/_jsx(Alert, {
          type: panelAlert.type,
          closeable: true,
          onClose: () => setPanelAlert(null),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: panelAlert.messageKey
          })
        })
      })]
    })
  });
}